import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  useCameras,
  useDisableZoom,
  useError,
  useMonitor,
  useTimer,
} from "../../../../hooks";
import {
  Box,
  CircularProgress,
  circularProgressClasses,
  CircularProgressProps,
  Stack,
  Typography,
} from "@mui/material";
import { SessionState } from "@biosensesignal/web-sdk";
import AwaitCameraPermissions from "../AwaitCameraPermissions";
import { HealthCheckQuestionnaireData } from "../../../../../../services/core-api-adapter";
import { useTranslation } from "react-i18next";
import { debugLogger } from "@/utils";

function useIsScannerMounted() {
  const isMounted = useRef<boolean>(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);
  return () => isMounted.current;
}

interface ScannerProps {
  onError: (error: any) => void;
  onScanReady: () => void;
  onScanComplete: (vitalSigns: any) => void;
  onStartMeasuring: boolean;
  measurementDuration: number;
  onBackButton: (error: any) => void;
  healthCheckQuestionnaireData: HealthCheckQuestionnaireData | null;
  onVitalSignsChanged?: (vitalSigns: any) => void;
  /** When provided, use this camera (controlled). Enables toggle before scan. */
  cameraId?: string;
  /** When true, mirror the video (front-facing). When false, do not mirror (back-facing). Default true. */
  mirrorVideo?: boolean;
  /** Called when the list of available cameras is ready. */
  onCamerasReady?: (cameras: MediaDeviceInfo[]) => void;
}

function CircularProgressWithLabel(
  props: CircularProgressProps & {
    value: number;
    showLabel: boolean;
    children?: React.ReactNode;
  }
) {
  const PROGRESS_SIZE = "23rem";

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        width: PROGRESS_SIZE,
        height: PROGRESS_SIZE,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: PROGRESS_SIZE,
          height: PROGRESS_SIZE,
          overflow: "hidden",
          borderRadius: "50%",
        }}
      >
        {props.children != null && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          >
            {props.children}
          </Box>
        )}
        <CircularProgress
          variant="determinate"
          sx={{
            color: "white",
            position: "relative",
            zIndex: 1,
          }}
          size={PROGRESS_SIZE}
          thickness={1}
          value={100}
        />

        <CircularProgress
          variant={props.variant || "determinate"}
          disableShrink
          sx={{
            animationDuration: "550ms",
            position: "absolute",
            left: 0,
            zIndex: 1,
            [`& .${circularProgressClasses.circle}`]: {
              strokeLinecap: "round",
            },
          }}
          value={props.value}
          color={props.color}
          size={PROGRESS_SIZE}
          thickness={1.2}
        />
      </Box>
      {props.showLabel && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translate(-50%, -40%)",
            borderRadius: 5,
            width: (theme) => theme.spacing(8),
            background: "white",
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            py: 1,
            justifyContent: "center",
            display: "flex",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: () =>
                props.color === "success" ? "success.main" : "primary.main",
            }}
          >{`${Math.round(props.value)}%`}</Typography>
        </Box>
      )}
    </Box>
  );
}

export default function Scanner({
  onVitalSignsChanged,
  onError,
  onStartMeasuring,
  measurementDuration,
  onScanReady,
  onScanComplete,
  healthCheckQuestionnaireData,
  onBackButton,
  cameraId: cameraIdProp,
  mirrorVideo = true,
  onCamerasReady,
}: ScannerProps) {
  const { t } = useTranslation();
  const cameras = useCameras();
  const [internalCameraId, setInternalCameraId] = useState<string>("");
  const [videoStreamTrack, setVideoStreamTrack] = useState<any>();

  const cameraId = cameraIdProp ?? internalCameraId;
  const licenseKey = import.meta.env.VITE_APP_BINAH_AI_SDK_LICENSE_KEY || "";
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [measurementProgress, setMeasurementProgress] = useState(0);
  const [alertMessage, setAlertMessage] = useState("");

  const mounted = useIsScannerMounted();
  useDisableZoom();

  const { sessionState, vitalSigns, error, info, isScanComplete } = useMonitor(
    videoRef as any,
    cameraId,
    measurementDuration,
    licenseKey,
    onStartMeasuring,
    healthCheckQuestionnaireData
  );

  const errorMessage = useError(error);
  const isMeasuring = useCallback(
    () => sessionState === SessionState.MEASURING,
    [sessionState]
  );

  useEffect(() => {
    if (cameras?.length) {
      setInternalCameraId(cameras[0].deviceId);
      onCamerasReady?.(cameras);
    }
  }, [cameras, onCamerasReady]);

  useEffect(() => {
    const videoElement = document.getElementById(
      "video"
    ) as HTMLVideoElement | null;
    const stream =
      (videoElement?.srcObject as MediaStream | null) ??
      (sessionState === SessionState.ACTIVE
        ? (videoRef.current?.srcObject as MediaStream | null)
        : null);

    if (stream) {
      streamRef.current = stream;
      const tracks = stream.getTracks();
      setVideoStreamTrack(tracks[0]);
    }

    return () => {
      if (!mounted()) {
        const s = streamRef.current;
        streamRef.current = null;
        if (s?.getTracks) {
          s.getTracks().forEach((track) => track.stop());
        }
      }
    };
  }, [mounted, sessionState]);

  const seconds = useTimer(onStartMeasuring, measurementDuration);

  function getPercentage() {
    const computedPercentage = seconds / measurementDuration;
    const percentage = Math.floor(computedPercentage * 100);
    return percentage;
  }

  useEffect(() => {
    if (getPercentage() <= 100) {
      setMeasurementProgress(getPercentage());
    }
  }, [seconds]);

  useEffect(() => {
    if (error) {
      onError(error);
    }

    if (info) {
      if (info.type <= 0) {
        setAlertMessage("");
      }

      if (info.message) {
        debugLogger.faceScan("warningEncountered", {
          warningMessage: info.message,
          source: "Health check",
        });
        setAlertMessage(info.message);
      }
    }
  }, [error, info]);

  useEffect(() => {
    if (sessionState === SessionState.ACTIVE) {
      onScanReady();
    }

    if (isMeasuring() && onVitalSignsChanged) {
      onVitalSignsChanged(vitalSigns);
    }

    if (isScanComplete === true && Object.keys(vitalSigns || {})?.length) {
      onScanComplete(vitalSigns);
    }
  }, [vitalSigns, sessionState, errorMessage, measurementDuration]);

  return (
    <>
      {!sessionState && (
        <Stack
          alignItems="center"
          sx={{
            width: "100%",
            height: "100%",
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 4,
          }}
        >
          <AwaitCameraPermissions onBackButtonClick={onBackButton} />
        </Stack>
      )}
      <Stack
        sx={{
          height: "100%",
          top: 0,
          zIndex: 0,
          backgroundColor: "neutral.dark",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <>
          <Stack
            sx={{
              position: "absolute",
              zIndex: 3,
              top: "20%",
              left: "50%",
              transform: "translate(-50%, 0)",
            }}
            spacing={2}
          >
            <CircularProgressWithLabel
              value={measurementProgress}
              showLabel={onStartMeasuring}
              variant="determinate"
              color={measurementProgress === 100 ? "success" : "primary"}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "100%",
                  aspectRatio: "3/4",
                  height: "auto",
                }}
              >
                <video
                  ref={videoRef}
                  id="video"
                  muted={true}
                  playsInline={true}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transformOrigin: "center",
                    transform: mirrorVideo ? "scaleX(-1)" : "none",
                  }}
                />
              </Box>
            </CircularProgressWithLabel>
            <Stack color="neutral.contrastText">
              <Typography variant="h2" textAlign="center" color="inherit">
                {alertMessage.match("Warning")
                  ? t("BinahScanFlow.measurenow.state.SCAN_IN_PROGRESS.title")
                  : alertMessage}
              </Typography>
            </Stack>
          </Stack>
        </>
      </Stack>
    </>
  );
}

export const MemoizedScanner = memo(Scanner);
